import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"

// Trust Center Step 5 "done for you" kit (#14654 / #8639): applies an insurance
// campaign template (repainting the generated description + samples in place)
// and uploads the opt-in evidence screenshot — both over AJAX so the wizard
// never reloads and loses its step. Persistence and validation are
// server-side (UserSettings::TrustCenterController#select_campaign_template /
// #upload_opt_in_evidence); this controller is UX.
export default class extends Controller {
  static targets = [
    "templateSelect", "applyBtn", "templateLabel", "description", "samples", "templateMessage",
    "evidenceForm", "evidenceInput", "uploadBtn", "evidenceMessage", "evidenceLink",
  ]

  static values = {
    templateUrl: String,
    evidenceUrl: String,
  }

  async applyTemplate(event) {
    event.preventDefault()
    if (!this.hasTemplateSelectTarget) return

    const key = this.templateSelectTarget.value
    await this.withBusy(this.applyBtnTarget, async () => {
      const response = await post(this.templateUrlValue, {
        body: JSON.stringify({ template_key: key }),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = await response.json
      if (data.success) {
        this.renderSummary(data.summary)
        if (this.hasTemplateLabelTarget && data.template) this.templateLabelTarget.textContent = data.template.label
        this.showMessage(this.templateMessageTarget, data.message || "Template applied.", "success")
        // resubmit: the server's recomputed Send My Fix gate (#14720), or null.
        this.dispatch("templateApplied", { detail: { key, resubmit: data.resubmit ?? null } })
      } else {
        this.showMessage(this.templateMessageTarget, data.message || "Could not apply the template.", "error")
      }
    }, this.templateMessageTarget)
  }

  async uploadEvidence(event) {
    event.preventDefault()
    if (!this.hasEvidenceFormTarget) return
    if (this.hasEvidenceInputTarget && this.evidenceInputTarget.files.length === 0) {
      this.showMessage(this.evidenceMessageTarget, "Choose a screenshot to upload.", "error")
      return
    }

    await this.withBusy(this.uploadBtnTarget, async () => {
      const response = await post(this.evidenceUrlValue, {
        body: new FormData(this.evidenceFormTarget),
        responseKind: "json",
      })
      const data = await response.json
      if (data.success) {
        this.showMessage(this.evidenceMessageTarget, data.message || "Screenshot saved.", "success")
        if (this.hasEvidenceLinkTarget && data.url) {
          this.evidenceLinkTarget.href = data.url
          this.evidenceLinkTarget.textContent = data.filename || "View screenshot"
          this.evidenceLinkTarget.hidden = false
        }
        // Carrier Approval's campaign Send My Fix re-opens on this (#18783).
        this.dispatch("evidenceSaved", { detail: { resubmit: data.resubmit ?? null } })
      } else {
        this.showMessage(this.evidenceMessageTarget, data.message || "Could not save the screenshot.", "error")
      }
    }, this.evidenceMessageTarget)
  }

  // Repaints the generated summary blocks from the server's regenerated
  // payload — the same text RegisterCampaign will send, never a client-side
  // rendering of the template.
  renderSummary(summary) {
    if (!summary) return
    if (this.hasDescriptionTarget && summary.description) {
      this.descriptionTarget.textContent = summary.description
    }
    if (this.hasSamplesTarget && Array.isArray(summary.message_samples)) {
      this.samplesTarget.replaceChildren(
        ...summary.message_samples.map((sample) => {
          const item = document.createElement("li")
          item.className = "gl-form-hint"
          item.textContent = sample
          return item
        })
      )
    }
  }

  // BUTTON-8: the label stays put while the request runs — shared spinner,
  // aria-busy, second click blocked (lib/button_loading).
  async withBusy(button, work, messageTarget) {
    setButtonLoading(button, true)
    try {
      await work()
    } catch {
      this.showMessage(messageTarget, "Something went wrong. Please try again.", "error")
    } finally {
      setButtonLoading(button, false)
    }
  }

  showMessage(target, text, type) {
    if (!target) return
    const tone = { success: "gl-text-success", info: "text-muted", error: "gl-text-danger" }[type] || "gl-text-danger"
    target.textContent = text
    target.className = `gl-form-hint ${tone}`
  }
}
